/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Alert from '@mui/material/Alert';
import { isNilOrEmpty } from 'ramda-extension';
import { connect } from 'react-redux';

import { getLabelMessage } from '@gef-ui/localization/messages';

import Panel from 'components/atoms/GefMui/Panel/Panel';
import PaperWithToolbar from 'components/atoms/GefMui/PaperWithToolbar';
import { ComponentLoader } from 'components/index';
import PAGES from 'constants/pages';
import fetch from 'features/common/fetch';
import { subheaders } from 'features/common/Subheader/constants';
import campaignDetail from 'features/views/campaignManagement/campaignDetail';
import nominatedSellers from 'features/views/campaignManagement/nominatedSellers';
import campaignNominations from 'features/views/campaignNominations';
import { getFilteredNominationRegions } from 'features/views/campaignRegionNomination/selectors';
import user from 'features/views/user';

import Filter from './Filter';
import Region from './Region';
import Toolbar from './Toolbar';

const CampaignRegionNominations = (props) => {
	const {
		fetchNominatedSellers,
		fetchCampaignDetail,
		fetchNominationsCount,
		campaignDetail,
		campaignId,
		nominationRegions,
	} = props;

	const [countsByLayer, setCountsByLayer] = useState({});

	useEffect(() => {
		fetchCampaignDetail(campaignId);
		fetchNominatedSellers(campaignId);
		fetchNominationsCount(campaignId).then((nominationCountsByLayer) => {
			setCountsByLayer(nominationCountsByLayer);
		});
	}, [campaignId]);

	const handleReload = useCallback(() => {
		fetchCampaignDetail(campaignId);
		fetchNominationsCount(campaignId).then((nominationCountsByLayer) => {
			setCountsByLayer(nominationCountsByLayer);
		});
	}, [campaignId]);

	const noResults = useMemo(() => <Alert severity="info">{getLabelMessage('error:noResults')}</Alert>, []);

	return (
		<ComponentLoader fetchStorePath={`${fetch.constants.urls.orgStructureRegions}`} onReload={handleReload}>
			<ComponentLoader
				fetchStorePath={fetch.constants.urls.campaignDetailWithoutConsideringRole}
				onReload={handleReload}
				renderOnlyIfLoaded
			>
				<ComponentLoader fetchStorePath={fetch.constants.urls.campaignNominationsCount} onReload={handleReload}>
					<Panel
						screenId={PAGES.campaignRegionNominations.id}
						screenName={PAGES.campaignRegionNominations.name}
					>
						<PaperWithToolbar
							toggledElement={<Filter />}
							toolbar={<Toolbar campaignDetail={campaignDetail} />}
							collapsibleProps={{ id: subheaders.campaignRegionNominations }}
						>
							{!isNilOrEmpty(nominationRegions)
								? nominationRegions.map((region) => (
										<Region
											key={`${region.id} ${nominationRegions.length === 1}`}
											region={region}
											campaignDetail={campaignDetail}
											count={countsByLayer[region.id]}
											isTheOnlyRegion={nominationRegions.length === 1}
										/>
								  ))
								: noResults}
						</PaperWithToolbar>
					</Panel>
				</ComponentLoader>
			</ComponentLoader>
		</ComponentLoader>
	);
};

CampaignRegionNominations.propTypes = {
	campaignDetail: PropTypes.object,
	campaignId: PropTypes.number,
	fetchCampaignDetail: PropTypes.func,
	fetchNominatedSellers: PropTypes.func,
	fetchNominationsCount: PropTypes.func,
	nominationRegions: PropTypes.array,
};

export default connect(
	(state, ownProps) => {
		const allowedRegionsWithCampaign = ownProps.campaignId
			? user.selectors.getAllowedRegionsWithCampaign(state, ownProps.campaignId)
			: user.selectors.getAllowedRegions(state);
		const nominationRegions = getFilteredNominationRegions(state, allowedRegionsWithCampaign);

		return {
			campaignDetail: campaignDetail.selectors.getCampaignDetail(state),
			nominationRegions,
		};
	},
	{
		fetchNominatedSellers: nominatedSellers.actions.fetchNominatedSellers,
		fetchCampaignDetail: campaignNominations.actions.fetchCampaignDetail,
		fetchNominationsCount: campaignNominations.actions.fetchNominationsCount,
	},
)(CampaignRegionNominations);
